// Sitemap.jsx — Visual knowledge-graph node map of Search.Inc
//
// Brand-aligned design: dark navy canvas, cyan glow, subtle starfield,
// SVG connection lines, hover/pulse interactions.  Echoes the
// knowledge-graph icon in the site header so users recognise this as
// "the way Search.Inc thinks about pages."
//
// SEO-friendly: pure SVG + semantic HTML, no JS-only rendering, all
// internal nodes are anchor tags Google can crawl.
//
// v5.7 — Feb 27, 2026.  Iteration after user review:
//   • Lines were missing on the horizontal (Privacy/Login) connections
//     because the linearGradient with default objectBoundingBox fails
//     on zero-height bounding boxes.  Switched to a solid stroke with
//     stroke-opacity so all 5 connections render reliably.
//   • Labels repositioned OUTWARD relative to the hub so descriptions
//     no longer cross connection lines.
//   • "public sitemap" wording removed (this is just the site map; the
//     pages aren't a separate "public" thing from the rest of the site).
//   • Back button reframed: this IS Search.Inc, not a place we leave to.

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Home, LogIn, Scale, FileText, ShieldCheck, AlertTriangle, ArrowLeft, Compass } from 'lucide-react';

// Each outer node carries a `labelAnchor` that says where the label
// should sit relative to the icon-circle.  Values: 'top' | 'bottom'
// | 'left' | 'right'.  Picked so the label always sits OUTWARD from
// the hub, never crossing a connection line.
const PAGES = [
  // D5-3 (2026-02-13) — outer-node label anchor rule per operator:
  // "Those on the left side of central bubble present on left of their
  //  bubble. Those on right present on right of their bubble."
  // Angles used: 90=right, 30=top-right, 330=top-left, 270=left,
  // 210=bottom-left, 150=bottom-right.
  //
  // SPD v5.79 (2026-02-14) — descWidth values re-tuned:
  //   • Right-anchored (Ethics/Login/Atlas): widened so copy wraps
  //     into fewer lines, hangs BELOW the label with left edge on the
  //     label's FIRST letter (K/E/L). Right edge sits inside 1920 vw.
  //   • Left-anchored (AI Risk/Privacy/Terms): description hangs
  //     BELOW the label but extends LEFTWARD from the label's LAST
  //     letter (y/y/e). Widths preserved because the leftward area
  //     is empty until it hits the viewport left edge (graph is
  //     centred, ~550 px of slack on the 1920 vw).
  { id: 'login',    label: 'Login',                    href: '/',           Icon: LogIn,         angle: 90,  labelAnchor: 'right',  descWidth: 500, description: 'Sign in to the user-first AI search engine. Protected by Cloudflare Turnstile' },
  { id: 'ethics',   label: 'Ethics Policy',            href: '/?open=ethics',  Icon: Scale,         angle: 30,  labelAnchor: 'right',  descWidth: 700, description: 'How Search.Inc behaves when nobody is watching. Trustworthy AI by design.' },
  { id: 'aiRisk',   label: 'AI & Data Risk Policy',    href: '/?open=aiRisk',  Icon: AlertTriangle, angle: 330, labelAnchor: 'left',   descWidth: 500, description: 'What AI gets wrong, and what we do about it. AI transparency and explainability you can audit.' },
  { id: 'privacy',  label: 'Privacy Policy',           href: '/?open=privacy', Icon: ShieldCheck,   angle: 270, labelAnchor: 'left',   descWidth: 500, description: 'We don\u2019t know who you are \u2014 and we like it that way. Anonymous search, no tracking, full data ownership.' },
  { id: 'terms',    label: 'Terms of Service',         href: '/?open=terms',   Icon: FileText,      angle: 210, labelAnchor: 'left',   descWidth: 500, description: 'The contract between you and Search.Inc \u2014 including your data-ownership rights.' },
  // SPD v5.72 (2026-02-11) — Public-facing Knowledge Atlas surface.
  // Placed at angle 150 (bottom-right, symmetric to Terms at 210) so
  // the six-node graph reads as a balanced hexagon.
  { id: 'atlas',    label: 'Knowledge Atlas',          href: '/knowledge-atlas', Icon: Compass,     angle: 150, labelAnchor: 'right', descWidth: 700, description: 'Any file or Document you upload into your Knowledge Graph is available within your own personal Wiki. It lets you understand, utilise and use any information that\u2019s within it. AND it\u2019s not just a wiki it\u2019s a self-learning memory system.  It learns and gets smarter as you provide more information on any topic you wish to add. This is only accessible by you.  Check it out.' },
];

const CENTER = { id: 'home', label: 'Search.Inc', href: '/', Icon: Home, description: 'Actually Not Evil' };

const RADIUS = 36; // % of canvas
function polar(angleDeg) {
  const rad = (angleDeg - 90) * (Math.PI / 180); // -90° so 0° is top
  return {
    x: 50 + RADIUS * Math.cos(rad),
    y: 50 + RADIUS * Math.sin(rad),
  };
}

// Convert labelAnchor + node position into absolute CSS for the label.
// Each anchor places the label so it sits OUTWARD from the hub.
//
// D5-4 / D5-5 (2026-02-13) — operator directive:
//   • "All outside labels should be the same distance away from their
//     bubble" — unified 16px offset on every side (was mixed 12/14 px).
//   • left/right anchors use top:50% + translateY(-50%) so the label +
//     description hug the bubble vertical mid-line (D5-4).
function labelPositionFor(anchor) {
  switch (anchor) {
    case 'top':    return { bottom: 'calc(100% + 16px)', top: 'auto',          left: '50%',            transform: 'translateX(-50%)' };
    case 'bottom': return { top: 'calc(100% + 16px)',    bottom: 'auto',       left: '50%',            transform: 'translateX(-50%)' };
    case 'left':   return { right: 'calc(100% + 16px)',  left: 'auto',         top: '50%',             transform: 'translateY(-50%)', textAlign: 'right' };
    case 'right':  return { left: 'calc(100% + 16px)',   right: 'auto',        top: '50%',             transform: 'translateY(-50%)', textAlign: 'left' };
    default:       return { top: 'calc(100% + 16px)',    left: '50%',          transform: 'translateX(-50%)' };
  }
}

const Sitemap = () => {
  // <title> + meta description + structured data for crawlers landing here directly.
  // Also overrides the site-wide noindex (set in index.html) — the sitemap is
  // a transparency artifact we WANT search engines to index, even while the
  // rest of the Beta app stays private. Option A SEO architecture, v5.8.
  useEffect(() => {
    const prev = document.title;
    document.title = 'Sitemap — Search.Inc';
    let meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute('content');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', 'A visual map of every page on Search.Inc — The First User-First, AI Agent / Search Engine for Canada And The World.');

    // Override site-wide noindex so /sitemap remains crawlable
    let robots = document.querySelector('meta[name="robots"]');
    const prevRobots = robots?.getAttribute('content');
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', 'index, follow, max-snippet:-1, max-image-preview:large');

    // BreadcrumbList JSON-LD — tells Google the sitemap is the parent of all
    // 5 outer pages, strengthening internal-link signal for SEO.  Plus a
    // CollectionPage wrapper to declare this page's role.
    const breadcrumbsJsonLd = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'CollectionPage',
          '@id': 'https://beta.search.inc/sitemap#collection',
          name: 'Sitemap — Search.Inc',
          url: 'https://beta.search.inc/sitemap',
          description: 'Every page on Search.Inc, mapped as a knowledge graph.',
          isPartOf: { '@id': 'https://beta.search.inc/#website' },
          hasPart: PAGES.map((p) => ({
            '@type': 'WebPage',
            name: p.label,
            url: `https://beta.search.inc${p.href}`,
            description: p.description,
          })),
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Search.Inc', item: 'https://beta.search.inc/' },
            { '@type': 'ListItem', position: 2, name: 'Sitemap',    item: 'https://beta.search.inc/sitemap' },
          ],
        },
      ],
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.sitemapJsonld = '1';
    script.textContent = JSON.stringify(breadcrumbsJsonLd);
    document.head.appendChild(script);

    return () => {
      document.title = prev;
      if (prevDesc !== undefined) meta.setAttribute('content', prevDesc);
      if (prevRobots !== undefined) robots.setAttribute('content', prevRobots);
      const existing = document.querySelector('script[data-sitemap-jsonld="1"]');
      if (existing) existing.remove();
    };
  }, []);

  return (
    <main
      data-testid="sitemap-page"
      className="relative min-h-screen w-full overflow-hidden text-slate-100"
      style={{
        background: 'radial-gradient(ellipse at center, #101b35 0%, #050811 70%)',
      }}
    >
      {/* Subtle starfield via repeating-radial — no JS, no extra assets */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-50 pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(1px 1px at 25% 17%, rgba(125, 211, 252, 0.65), transparent 50%),' +
            'radial-gradient(1px 1px at 73% 62%, rgba(125, 211, 252, 0.45), transparent 50%),' +
            'radial-gradient(1.5px 1.5px at 14% 81%, rgba(56, 189, 248, 0.55), transparent 50%),' +
            'radial-gradient(1px 1px at 88% 22%, rgba(125, 211, 252, 0.5), transparent 50%),' +
            'radial-gradient(1px 1px at 47% 93%, rgba(165, 243, 252, 0.4), transparent 50%),' +
            'radial-gradient(1.2px 1.2px at 92% 78%, rgba(56, 189, 248, 0.6), transparent 50%),' +
            'radial-gradient(1px 1px at 8% 38%, rgba(125, 211, 252, 0.5), transparent 50%)',
        }}
      />

      {/* Top bar — back to home (this IS Search.Inc, no copy implying departure) */}
      <header className="relative z-20 px-6 sm:px-12 pt-4 flex items-center justify-between">
        <Link
          to="/"
          data-testid="sitemap-back-home"
          className="inline-flex items-center gap-2 text-lg sm:text-xl text-cyan-300/80 hover:text-cyan-200 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 sm:h-6 sm:w-6" />
          <span>Back to home</span>
        </Link>
        <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-400/60">
          Site Map
        </div>
      </header>

      {/* Title block.
          D7 revision (2026-02-14) — operator directives:
            · "KNOWLEDGE MAP" chip removed; replaced with an H1 that
              reads "Site Map" at the original hero size.
            · "Every page, at a glance." now sits as a subheading at
              half the previous size.
            · The instructional paragraph ("Hover a node… Admin screens
              are intentionally excluded…") is deleted — operator noted
              it "never should have been there". */}
      <section className="relative z-20 px-6 sm:px-12 mt-3 sm:mt-4 max-w-3xl">
        <h1 className="text-3xl sm:text-5xl font-bold leading-tight text-cyan-100">
          Site Map
        </h1>
        <p className="mt-2 text-base sm:text-2xl text-cyan-100/80 leading-tight">
          Every page, at a glance.
        </p>
      </section>

      {/* The node graph — SAME 820 px size as before. Sole change:
          the whole section is shifted UP by exactly 3 cm via
          margin-top, per operator directive (2026-02-14). Nothing
          else touched — no shrink, no font change, no layout
          change. */}
      <section
        className="relative z-20 mx-auto mb-8"
        style={{ width: 'min(92vw, 820px)', aspectRatio: '1 / 1', marginTop: '-3cm' }}
        data-testid="sitemap-node-graph"
      >
        {/* SVG connection lines — sit BELOW the nodes via z-index */}
        <svg
          viewBox="0 0 100 100"
          className="absolute inset-0 w-full h-full pointer-events-none"
          preserveAspectRatio="xMidYMid meet"
          aria-hidden="true"
        >
          <defs>
            <radialGradient id="hubGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%"  stopColor="#22d3ee" stopOpacity="0.50" />
              <stop offset="60%" stopColor="#22d3ee" stopOpacity="0.10" />
              <stop offset="100%" stopColor="#22d3ee" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Hub glow halo */}
          <circle cx="50" cy="50" r="20" fill="url(#hubGlow)" />

          {/* Connection lines — solid cyan with opacity.
              Switched from linearGradient (which failed on horizontal
              zero-height bounding boxes) to a solid stroke so every
              connection renders consistently. */}
          {PAGES.map((p) => {
            const { x, y } = polar(p.angle);
            return (
              <g key={p.id}>
                <line
                  x1="50" y1="50"
                  x2={x} y2={y}
                  stroke="#22d3ee"
                  strokeOpacity="0.55"
                  strokeWidth="0.4"
                  strokeLinecap="round"
                />
                {/* Pulse dot travelling along the line — pure CSS keyframe */}
                <circle
                  r="0.55"
                  fill="#67e8f9"
                  className="sitemap-pulse-dot"
                  style={{
                    offsetPath: `path('M 50 50 L ${x} ${y}')`,
                    animationDelay: `${PAGES.indexOf(p) * 0.7}s`,
                  }}
                />
              </g>
            );
          })}
        </svg>

        {/* Center hub — Search.Inc */}
        <Link
          to={CENTER.href}
          data-testid={`sitemap-node-${CENTER.id}`}
          className="group absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          aria-label="Search.Inc — Home"
        >
          <div
            className="relative w-32 h-32 sm:w-40 sm:h-40 rounded-full flex flex-col items-center justify-center gap-1.5 transition-all duration-300 group-hover:scale-105"
            style={{
              background: 'radial-gradient(circle at 30% 30%, rgba(34,211,238,0.35) 0%, rgba(8,18,38,0.95) 70%)',
              border: '1.5px solid rgba(103,232,249,0.55)',
              boxShadow: '0 0 36px rgba(34,211,238,0.40), inset 0 0 20px rgba(34,211,238,0.18)',
            }}
          >
            <CENTER.Icon className="h-7 w-7 sm:h-9 sm:w-9 text-cyan-200" />
            <span className="text-sm sm:text-base font-semibold text-cyan-100 tracking-wide">
              {CENTER.label}
            </span>
            <span className="text-[10px] uppercase tracking-widest text-cyan-300/60">
              Home
            </span>
          </div>
        </Link>

        {/* Outer nodes — labels positioned OUTWARD from hub */}
        {PAGES.map((p) => {
          const { x, y } = polar(p.angle);
          return (
            <Link
              key={p.id}
              to={p.href}
              data-testid={`sitemap-node-${p.id}`}
              className="group absolute -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${x}%`, top: `${y}%` }}
              aria-label={`${p.label} — ${p.description}`}
            >
              {/* Icon circle */}
              <div
                className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                style={{
                  background: 'radial-gradient(circle at 30% 30%, rgba(34,211,238,0.22) 0%, rgba(15,23,42,0.95) 70%)',
                  border: '1px solid rgba(103,232,249,0.40)',
                  boxShadow: '0 0 18px rgba(34,211,238,0.25)',
                }}
              >
                <p.Icon className="h-6 w-6 sm:h-7 sm:w-7 text-cyan-200 transition-colors group-hover:text-cyan-100" />
                <div
                  className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{
                    boxShadow: '0 0 28px rgba(34,211,238,0.55), inset 0 0 14px rgba(34,211,238,0.30)',
                    pointerEvents: 'none',
                  }}
                />
              </div>

              {/* Label + description as a stacked block positioned OUTWARD from the hub.
                  SPD v5.79 (2026-02-14) — operator directive:
                    · Description hangs BELOW the label (never beside).
                    · For RIGHT-anchored nodes (Ethics/Login/KA):
                      description's LEFT edge aligns with the label's
                      FIRST letter (K in Knowledge, L in Login, E in
                      Ethics). Description extends RIGHTWARD.
                    · For LEFT-anchored nodes (Privacy/Terms/AI Risk):
                      description's RIGHT edge aligns with the label's
                      LAST letter (y in Policy, e in Service). Text is
                      right-aligned so it extends LEFTWARD into the
                      empty half of the canvas.
                  Implementation: the inner wrapper uses
                  `whitespace-nowrap` to shrink-wrap to label width, so
                  `left: 0` == label's first letter and `right: 0` ==
                  label's last letter regardless of label length. */}
              <div
                className="absolute pointer-events-none"
                style={
                  p.labelAnchor === 'left'
                    ? { right: 'calc(100% + 16px)', top: '50%', transform: 'translateY(-50%)' }
                    : { left: 'calc(100% + 16px)',  top: '50%', transform: 'translateY(-50%)' }
                }
              >
                <div className="relative whitespace-nowrap">
                  <div className="text-xl font-semibold text-cyan-100 leading-tight">
                    {p.label}
                  </div>
                  <div
                    className="absolute text-lg font-normal text-cyan-400 leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-300 pt-2"
                    style={{
                      top: '100%',
                      // LEFT-anchored: pin the description's RIGHT edge to the
                      // label's RIGHT edge (last letter) and let it flow left.
                      // RIGHT-anchored: pin LEFT edge to the label's LEFT edge
                      // (first letter) and let it flow right.
                      ...(p.labelAnchor === 'left'
                        ? { right: 0, textAlign: 'right' }
                        : { left: 0,  textAlign: 'left' }),
                      width: `${p.descWidth || 500}px`,
                      whiteSpace: 'normal',
                    }}
                  >
                    {p.description}
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </section>

      {/* Local keyframes — pulse dot animation */}
      <style>{`
        @keyframes sitemap-pulse-travel {
          0%   { offset-distance: 0%;   opacity: 0;   }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { offset-distance: 100%; opacity: 0;   }
        }
        .sitemap-pulse-dot {
          animation: sitemap-pulse-travel 4.5s linear infinite;
          filter: drop-shadow(0 0 1.5px #67e8f9);
        }
      `}</style>
    </main>
  );
};

export default Sitemap;
